import { Inject, Injectable } from '@angular/core'
import { DOCUMENT } from '@angular/common'
import { Meta, Title } from '@angular/platform-browser'
import { RouterStateSnapshot, TitleStrategy } from '@angular/router'

/** The one address search engines should index - corpers.ng and every deploy preview point here. */
export const SITE_URL = 'https://www.corpers.ng'
export const SITE_NAME = 'corpers.ng'
// Worded from Search Console: people search "corpers", "corpers lodge" and "ppa" - not "accommodation".
export const DEFAULT_TITLE = 'corpers.ng – Corpers Lodge, PPA Reviews & Marketplace'
export const DEFAULT_DESCRIPTION =
  'Find a corpers lodge, read PPA reviews, buy and sell, and chat with corpers in your state. ' +
  'The independent community for NYSC corps members.'
/** 1200×630, the size link previews expect - the logo on white (assets/images/logo_transparent_rectangle.png is too small on its own). */
export const DEFAULT_IMAGE = `${SITE_URL}/assets/images/share-card.png`

export type PageSeo = {
  /** Without the " | corpers.ng" suffix - it's added here. Leave out for the home page's title. */
  title?: string
  description?: string
  /** Absolute URL of the share image. */
  image?: string
  /** Path of the page's canonical URL, e.g. '/s/abc'. Query strings and fragments are dropped. */
  path: string
  /**
   * Keep this page out of search results - for "not found" pages, which still get HTTP 200 from
   * the SPA and would otherwise be indexed as soft 404s. Every navigation clears it.
   */
  noindex?: boolean
}

/**
 * The page's <title>, meta description, canonical URL and Open Graph / Twitter tags. index.html has
 * the defaults for crawlers that don't run JS; this keeps them in step as the app navigates.
 * Route-level values come from `title` and `data.description` (see SeoTitleStrategy); pages whose
 * details load from the API (public listings) call setPage() once they have them.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  setPage({ title, description, image, path, noindex = false }: PageSeo) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
    const desc = truncate(description || DEFAULT_DESCRIPTION, 160)
    const url = SITE_URL + (path.split(/[?#]/)[0] || '/')

    this.title.setTitle(fullTitle)
    this.meta.updateTag({ name: 'description', content: desc })
    this.meta.updateTag({ property: 'og:title', content: fullTitle })
    this.meta.updateTag({ property: 'og:description', content: desc })
    this.meta.updateTag({ property: 'og:url', content: url })
    this.meta.updateTag({ property: 'og:image', content: image || DEFAULT_IMAGE })
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle })
    this.meta.updateTag({ name: 'twitter:description', content: desc })
    this.meta.updateTag({ name: 'twitter:image', content: image || DEFAULT_IMAGE })
    this.setCanonical(url)

    if (noindex) this.meta.updateTag({ name: 'robots', content: 'noindex' })
    else this.meta.removeTag('name="robots"')
  }

  private setCanonical(url: string) {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = this.document.createElement('link')
      link.rel = 'canonical'
      this.document.head.appendChild(link)
    }
    link.href = url
  }
}

/** Replaces the router's default title handling, so every navigation also resets the meta tags. */
@Injectable()
export class SeoTitleStrategy extends TitleStrategy {
  constructor(private seo: SeoService) {
    super()
  }

  override updateTitle(snapshot: RouterStateSnapshot) {
    let route = snapshot.root
    while (route.firstChild) route = route.firstChild

    this.seo.setPage({
      title: this.buildTitle(snapshot),
      description: route.data['description'],
      path: route.data['canonicalPath'] ?? snapshot.url,
    })
  }
}

function truncate(text: string, max: number): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length <= max ? flat : flat.slice(0, max - 1).trimEnd() + '…'
}
